import html2canvas from 'html2canvas';

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function parseCssNumber(value: string): number {
  const normalized = value.trim();
  if (!normalized) return 0;
  if (normalized.endsWith('%')) {
    return Number(normalized.slice(0, -1)) / 100;
  }
  return Number(normalized);
}

function oklchToRgb(value: string): string {
  try {
    // Value is already extracted from oklch(...), so it's just "0.145 0 0" or "0.145 0 0 / 0.5"
    const [body, alphaValue] = value.split('/');
    const parts = body
      .trim()
      .split(/[\s,]+/) // Split by spaces or commas
      .filter(Boolean);

    if (parts.length < 3) {
      // Fallback to transparent black if parsing fails
      return 'rgba(0, 0, 0, 0)';
    }

    const lightness = parseCssNumber(parts[0]);
    const chroma = parseCssNumber(parts[1]);
    const hue = parseCssNumber(parts[2]);
    const alpha = alphaValue ? clamp(parseCssNumber(alphaValue), 0, 1) : 1;

    const a = chroma * Math.cos((hue * Math.PI) / 180);
    const b = chroma * Math.sin((hue * Math.PI) / 180);

    const l_ = lightness + 0.3963377774 * a + 0.2158037573 * b;
    const m_ = lightness - 0.1055613458 * a - 0.0638541728 * b;
    const s_ = lightness - 0.0894841775 * a - 1.2914855480 * b;

    const l3 = l_ * l_ * l_;
    const m3 = m_ * m_ * m_;
    const s3 = s_ * s_ * s_;

    const rLinear = 4.0767416621 * l3 - 3.3077115913 * m3 + 0.2309699292 * s3;
    const gLinear = -1.2684380046 * l3 + 2.6097574011 * m3 - 0.3413193965 * s3;
    const bLinear = -0.0041960863 * l3 - 0.7034186147 * m3 + 1.7076147010 * s3;

    const toSrgb = (channel: number): number => {
      const normalized = channel <= 0.0031308 ? 12.92 * channel : 1.055 * Math.pow(channel, 1 / 2.4) - 0.055;
      return clamp(Math.round(normalized * 255), 0, 255);
    };

    const red = toSrgb(rLinear);
    const green = toSrgb(gLinear);
    const blue = toSrgb(bLinear);

    return alpha < 1 ? `rgba(${red}, ${green}, ${blue}, ${alpha})` : `rgb(${red}, ${green}, ${blue})`;
  } catch (err) {
    // If conversion fails, return transparent black as safe fallback
    console.warn('[FeedbackHub] Failed to convert oklch color:', value, err);
    return 'rgba(0, 0, 0, 0)';
  }
}

export function sanitizeCssForCapture(cssText: string): string {
  let result = cssText;
  
  // Convert oklch() colors to rgb
  result = result.replace(/oklch\(([^)]+)\)/gi, (_match, body: string) => oklchToRgb(body));
  
  // Convert lch() colors to black as fallback (lch needs more complex conversion)
  result = result.replace(/lch\([^)]*\)/gi, 'rgb(0, 0, 0)');
  
  // Convert lab() colors to black as fallback (lab needs complex conversion)
  result = result.replace(/lab\([^)]*\)/gi, 'rgb(0, 0, 0)');
  
  // Convert hwb() to black as fallback (hwb needs conversion logic)
  result = result.replace(/hwb\([^)]*\)/gi, 'rgb(0, 0, 0)');
  
  // Convert color-mix() to black as fallback
  result = result.replace(/color-mix\([^)]*\)/gi, 'rgb(0, 0, 0)');
  
  // Convert color() function (CSS Color Module Level 4) to black as fallback
  result = result.replace(/color\([^)]*\)/gi, 'rgb(0, 0, 0)');
  
  return result;
}

function sanitizeCloneForCapture(documentClone: Document): void {
  const styleElements = Array.from(documentClone.querySelectorAll('style'));
  for (const styleElement of styleElements) {
    styleElement.textContent = sanitizeCssForCapture(styleElement.textContent || '');
  }

  const elementsWithInlineStyles = Array.from(documentClone.querySelectorAll<HTMLElement>('*'));
  for (const element of elementsWithInlineStyles) {
    const styleValue = element.getAttribute('style');
    if (styleValue) {
      element.setAttribute('style', sanitizeCssForCapture(styleValue));
    }
  }

  try {
    const stylesheetOverrides = documentClone.createElement('style');
    stylesheetOverrides.setAttribute('data-fh-capture-sanitized', 'true');

    const cssRules: string[] = [];
    for (const sheet of Array.from(documentClone.styleSheets)) {
      try {
        for (const rule of Array.from(sheet.cssRules)) {
          cssRules.push(sanitizeCssForCapture(rule.cssText));
        }
      } catch {
        // Ignore stylesheets that cannot be read from the cloned document.
      }
    }

    stylesheetOverrides.textContent = cssRules.join('\n');
    documentClone.head.appendChild(stylesheetOverrides);
  } catch {
    // Ignore clone sanitization failures and continue with the capture.
  }
}

/**
 * Captures a screenshot of the current page using html2canvas.
 * Returns a canvas element that can be used for annotation.
 */
export async function captureScreenshot(): Promise<HTMLCanvasElement> {
  // Hide the feedback widget itself during capture
  const widgetRoot = document.getElementById('fh-widget-root');
  if (widgetRoot) widgetRoot.style.display = 'none';

  try {
    await new Promise<void>((resolve) => {
      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => setTimeout(resolve, 0));
      } else {
        setTimeout(resolve, 0);
      }
    });

    const canvas = await html2canvas(document.body, {
      useCORS: true,
      allowTaint: false,
      logging: false,
      scale: window.devicePixelRatio || 1,
      width: window.innerWidth,
      height: window.innerHeight,
      scrollX: -window.scrollX,
      scrollY: -window.scrollY,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      onclone: (documentClone) => {
        sanitizeCloneForCapture(documentClone);
      },
    });
    return canvas;
  } catch (err) {
    const errorMsg = err instanceof Error ? err.message : String(err);
    console.error('[FeedbackHub] Screenshot capture failed:', errorMsg);
    throw err;
  } finally {
    if (widgetRoot) widgetRoot.style.display = '';
  }
}

/**
 * Converts a canvas to a PNG Blob.
 */
export function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to convert canvas to blob'));
      },
      'image/png',
      0.92,
    );
  });
}
